import { commandPaths, type Api, type ChangeSummary, type CommandAnswer, type CommandBody, type CommandName } from "@nabla/api";
import type { CommandParams, PersonDto, Route } from "@nabla/api";
import { loadApi, type Pick, type Schema } from "./answers.ts";
import { offered } from "./offered.ts";
import type { DurableEvent } from "./cursor.ts";
import { sendBytes, sendJson, signedOutBy, type ClientError, type Reach, type Reply } from "./http.ts";
import { clientCopy } from "./copy.ts";
import { createHolds } from "./held.ts";
import type { Hub } from "./hub.ts";
import { createNotices, type Notice } from "./notices.ts";

type Standing =
  | { readonly state: "segurando" | "enviando" | "pendente" }
  | { readonly state: "registrada"; readonly value?: unknown }
  | { readonly state: "falhou"; readonly error: ClientError };

export type CommandRecord = Standing & {
  readonly requestKey: string;
  readonly target: string;
  readonly verb?: string;
  readonly startedAt: number;
};

const conflicts: ReadonlySet<string> = new Set(["ja-decidida", "revisao-mudou", "regras-mudaram"]);
const conflicting = (error: ClientError): boolean => error.kind === "api" && conflicts.has(error.code);
export const conflictOf = (record: CommandRecord | undefined): boolean => record?.state === "falhou" && conflicting(record.error);

export type Settled = { readonly state: "registrada" } | { readonly state: "conflito" | "falhou"; readonly error: ClientError };

export function settledBetween(before: CommandRecord | undefined, now: CommandRecord | undefined): Settled | undefined {
  if (now?.requestKey !== before?.requestKey || (before?.state !== "enviando" && before?.state !== "pendente")) return undefined;
  if (now?.state === "falhou") return { state: conflictOf(now) ? "conflito" : "falhou", error: now.error };
  return now?.state === "registrada" ? { state: "registrada" } : undefined;
}

export type { Notice };
type Lost = (event: DurableEvent) => ClientError | undefined;

interface Tracking<T> {
  readonly target: string;
  readonly verb?: string;
  readonly effect?: (answer: T | null) => void;
  readonly settled?: ((settled: Settled) => void) | undefined;
  readonly lost?: Lost;
  readonly toast?: string | false | undefined;
  readonly taken?: Taken | undefined;
  readonly held?: string | undefined;
}

type Paramed<K extends CommandName> = [keyof CommandParams<K>] extends [never]
  ? { readonly params?: undefined }
  : { readonly params: CommandParams<K> };
type CommandSpec<K extends CommandName> = Paramed<K> & { readonly body: CommandBody<K> } & Tracking<CommandAnswer<K>>;
type Loose = { readonly params?: Readonly<Record<string, string>> | undefined; readonly body: object } & Tracking<unknown>;

type UploadName = keyof Api["uploads"];
type UploadSpec = { readonly upload: UploadName; readonly params: Readonly<Record<string, string>> } & Tracking<unknown> & {
  readonly name: string;
  readonly bytes: Blob;
  readonly query?: Readonly<Record<string, string>>;
};

export type Taken = (error: ClientError) => boolean;

export type Outcome<T> = { readonly ok: true; readonly value: T | null }
  | { readonly ok: false; readonly state: "pendente" | "descartada" } | { readonly ok: false; readonly state: "falhou"; readonly error: ClientError };

interface CommandsDeps {
  readonly host: Reach;
  readonly hub: Hub;
  readonly now: () => number;
  readonly randomKey: () => string;
  readonly signedOut: () => void;
  readonly generation: () => number;
  readonly after: (ms: number, run: () => void) => () => void;
}

export interface Commands {
  send<K extends CommandName>(name: K, spec: CommandSpec<K>): Sent<K>;
  offer(name: string, spec: Loose): Promise<Outcome<unknown>>;
  upload(spec: UploadSpec): Promise<Outcome<unknown>>;
  retry(requestKey: string): void;
  flush(): void;
  hold(text: string): Promise<boolean>;
  latest(target: string): CommandRecord | undefined;
  notice(): Notice | undefined;
  announce(kind: "conectado"): void;
  tell(text: string, open: { readonly label: string; readonly href: string }): void;
  undoable(text: string, undo: () => void): void;
  dismiss(id: number): void;
  eventApplied(event: DurableEvent): void;
  forget(): void;
}

interface Tracked {
  readonly confirmation: "event" | "answer";
  readonly about: Omit<CommandRecord, "state">;
  readonly record: CommandRecord;
  readonly run: () => void;
  readonly lost: Lost | undefined;
  readonly toast: string | false | undefined;
  readonly taken: Taken | undefined;
  readonly settled: ((settled: Settled) => void) | undefined;
}

type Deliver<T> = (requestKey: string, current: () => boolean) => Promise<Reply<T | null> | { readonly kind: "descartada" }>;
type Track = <T>(confirmation: Tracked["confirmation"], spec: Tracking<T>, deliver: Deliver<T>) => Promise<Outcome<T>>;

type Sent<K extends CommandName> = Promise<Outcome<CommandAnswer<K>>>;
function sendCommand<K extends CommandName>(deps: CommandsDeps, track: Track, name: K, spec: CommandSpec<K>): Sent<K>;
function sendCommand(deps: CommandsDeps, track: Track, name: CommandName, spec: Loose): Promise<Outcome<unknown>>;
function sendCommand(deps: CommandsDeps, track: Track, name: CommandName, spec: Loose): Promise<Outcome<unknown>> {
  const answer: Pick<unknown> = (api) => api.commands[name].answer ?? nothing;
  return track("event", spec, async (requestKey, current) => {
    const path = core(name) ? commandPaths[name] : await moduleCommand(name);
    if (!current()) return { kind: "descartada" };
    if (path === undefined) return { kind: "falhou", error: { kind: "formato", detail: `a rota de ${name} não carregou` } };
    return sendJson(deps.host, path.route, spec.params ?? {}, path.keyed ? { ...spec.body, requestKey } : spec.body, answer);
  });
}

const core = (name: CommandName): name is keyof typeof commandPaths => name in commandPaths;

async function moduleCommand(name: CommandName): Promise<{ readonly route: Route; readonly keyed: boolean } | undefined> {
  const api = await loadApi();
  return api === undefined ? undefined : { route: api.commands[name].route, keyed: api.keyed(name) };
}

const nothing: Schema<null> = { safeParse: () => ({ success: true, data: null }) };

function upload(deps: CommandsDeps, track: Track, given: UploadSpec): Promise<Outcome<unknown>> {
  const { upload: named, params, name, bytes, query = {}, ...spec } = given;
  return track("answer", spec, async (chave, current) => {
    const to = (await loadApi())?.uploads[named];
    if (!current()) return { kind: "descartada" };
    if (to === undefined) return { kind: "falhou", error: { kind: "formato", detail: `a rota de ${String(named)} não carregou` } };
    return sendBytes(deps.host, { route: to.route, params, query: { ...query, nome: name, chave } }, bytes, () => to.answer);
  });
}

interface Ledger {
  readonly deps: CommandsDeps;
  readonly tracked: Map<string, Tracked>;
  readonly byTarget: Map<string, string>;
  readonly notices: ReturnType<typeof createNotices>;
}

function move({ deps, tracked }: Ledger, requestKey: string, standing: Standing): void {
  const item = tracked.get(requestKey);
  if (!item) return;
  tracked.set(requestKey, { ...item, record: { ...item.about, ...standing } });
  deps.hub.touch(`command:${item.about.target}`);
}

function registered(ledger: Ledger, requestKey: string, value?: unknown): void {
  const was = ledger.tracked.get(requestKey);
  move(ledger, requestKey, value === undefined || value === null ? { state: "registrada" } : { state: "registrada", value });
  if (was === undefined || was.record.state === "registrada") return;
  was.settled?.({ state: "registrada" });
  if (was.toast !== false) ledger.notices.say(was.toast === undefined ? { kind: "registrada" } : { kind: "registrada", text: was.toast });
}

function failed(ledger: Ledger, requestKey: string, error: ClientError): void {
  const item = ledger.tracked.get(requestKey);
  move(ledger, requestKey, { state: "falhou", error });
  if (item === undefined) return;
  item.settled?.({ state: conflicting(error) ? "conflito" : "falhou", error });
  if (item.taken?.(error)) return;
  const retryable = error.kind === "rede" || (error.kind === "api" && error.retryable);
  ledger.notices.say(retryable ? { kind: "falhou", error, retry: item.run } : { kind: "falhou", error });
}

function takenBack({ deps, tracked, byTarget, notices }: Ledger, requestKey: string, target: string): void {
  tracked.delete(requestKey);
  if (byTarget.get(target) === requestKey) byTarget.delete(target);
  deps.hub.touch(`command:${target}`);
  notices.say({ kind: "registrada", text: clientCopy.desfeita });
}

type Holds = ReturnType<typeof createHolds>;

function holding({ notices }: Ledger, holds: Holds, requestKey: string, text: string, ends: (sent: boolean) => void): void {
  const toast = notices.say({ kind: "segurando", text, undo: () => void holds.end(requestKey, "desfazer") });
  holds.hold(requestKey, (how) => {
    if (toast !== undefined) notices.dismiss(toast);
    ends(how === "enviar");
  });
}

function tracker(ledger: Ledger, holds: Holds): Track {
  const { deps, tracked, byTarget } = ledger;
  return <T>(confirmation: Tracked["confirmation"], spec: Tracking<T>, deliver: Deliver<T>) => {
    const requestKey = deps.randomKey();
    const session = deps.generation();
    let resolve: (outcome: Outcome<T>) => void = () => undefined;
    const done = new Promise<Outcome<T>>((r) => (resolve = r));
    const current = () => deps.generation() === session;
    const run = () => {
      if (!current()) return resolve({ ok: false, state: "descartada" });
      move(ledger, requestKey, { state: "enviando" });
      void deliver(requestKey, current).then((answer) => {
        if (!current() || answer.kind === "descartada") return resolve({ ok: false, state: "descartada" });
        const state = tracked.get(requestKey)?.record.state;
        if (answer.kind === "feito") {
          spec.effect?.(answer.value);
          registered(ledger, requestKey, answer.value);
          return resolve({ ok: true, value: answer.value });
        }
        if (answer.kind === "pendente") {
          if (state === "enviando") move(ledger, requestKey, { state: "pendente" });
          return resolve({ ok: false, state: "pendente" });
        }
        signedOutBy(answer.error, deps.signedOut);
        if (state !== "registrada") failed(ledger, requestKey, answer.error);
        return resolve({ ok: false, state: "falhou", error: answer.error });
      });
    };
    const about = { requestKey, target: spec.target, startedAt: deps.now(), ...(spec.verb === undefined ? {} : { verb: spec.verb }) };
    const state = spec.held === undefined ? "enviando" : "segurando";
    tracked.set(requestKey, {
      confirmation, about, record: { ...about, state }, run, lost: spec.lost, toast: spec.toast, taken: spec.taken, settled: spec.settled,
    });
    byTarget.set(spec.target, requestKey);
    if (spec.held === undefined) run();
    else {
      deps.hub.touch(`command:${spec.target}`);
      holding(ledger, holds, requestKey, spec.held, (sent) => {
        if (sent && current()) return run();
        if (!sent) takenBack(ledger, requestKey, spec.target);
        resolve({ ok: false, state: "descartada" });
      });
    }
    return done;
  };
}

export function createCommands(deps: CommandsDeps): Commands {
  const ledger: Ledger = { deps, tracked: new Map(), byTarget: new Map(), notices: createNotices(deps.hub) };
  const { tracked, byTarget, notices } = ledger;
  const holds = createHolds(deps.after);
  const track = tracker(ledger, holds);

  return {
    send: (name, spec) => sendCommand(deps, track, name, spec),
    offer: (name, spec) => offered(deps.generation, name, (named) => sendCommand(deps, track, named, spec)),
    upload: (spec) => upload(deps, track, spec),
    retry: (requestKey) => tracked.get(requestKey)?.run(),
    flush: () => holds.every("enviar"),
    hold: (text) => new Promise((resolve) => holding(ledger, holds, deps.randomKey(), text, (sent) => {
      if (!sent) notices.say({ kind: "registrada", text: clientCopy.desfeita });
      resolve(sent);
    })),
    latest: (target) => tracked.get(byTarget.get(target) ?? "")?.record,
    notice: notices.notice,
    announce: (kind) => notices.say({ kind }),
    tell: (text, open) => void notices.say({ kind: "registrada", text, open }),
    undoable: (text, undo) => notices.undoable(text, undo, deps.after),
    dismiss: notices.dismiss,
    eventApplied(event) {
      for (const [requestKey, item] of tracked) {
        if (item.record.state !== "enviando" && item.record.state !== "pendente") continue;
        if (event.requestKey === requestKey) {
          if (item.confirmation === "event") registered(ledger, requestKey);
          continue;
        }
        const error = item.lost?.(event);
        if (error) failed(ledger, requestKey, error);
      }
    },
    forget() {
      holds.every("desfazer");
      const targets = [...byTarget.keys()];
      tracked.clear();
      byTarget.clear();
      for (const target of targets) deps.hub.touch(`command:${target}`);
      notices.forget();
    },
  };
}

export function decidedByAnother(proposal: string, me: () => PersonDto["id"] | undefined): Lost {
  return (event) => {
    if (event.type !== "mudanca" || event.change.id !== proposal || event.change.decidedBy === undefined) return undefined;
    const by = event.change.decidedBy;
    return by.kind === "pessoa" && by.id === me() ? undefined : decidedBefore(event.change);
  };
}

function decidedBefore(change: ChangeSummary): ClientError {
  const by = change.decidedBy;
  const who = by?.kind === "pessoa" ? { quem: "pessoa", nome: by.name } : by?.kind === "terminal" ? { quem: "terminal", nome: by.label } : { quem: "nabla" };
  const verbo = change.stage === "recusada" || change.stage === "retirada" ? "recusou" : "aprovou";
  return { kind: "api", status: 409, code: "ja-decidida", message: clientCopy.jaDecidida, retryable: false, context: { ...who, verbo, ...(change.decidedAt === undefined ? {} : { as: change.decidedAt }) } };
}
